import { Dialog, Transition } from '@headlessui/react'
import XMarkIcon from '@heroicons/react/20/solid/XMarkIcon'
import type { PropsWithChildren } from 'react'
import { Fragment, createContext, useContext } from 'react'
import { createPortal } from 'react-dom'
import { Button } from '../form/Button'
import { useIsHydrated } from '~/utils/useIsHydrated'
export type ModalContextValue = {
  visible: boolean
  onClose: () => void
  onConfirm?: () => void
}

const Context = createContext<ModalContextValue>({
  visible: false,
  onClose: () => {},
  onConfirm: () => {},
})

export type ModalProps = PropsWithChildren<ModalContextValue>

export const Modal = ({ visible, onClose, onConfirm, children }: ModalProps) => {
  const isHydrated = useIsHydrated()

  const dialog = (
    <Transition.Root show={visible} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-overlay/75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden p-4 sm:p-6">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <Dialog.Panel className="relative w-full max-h-[calc(100dvh-2rem)] max-w-md transform overflow-y-auto rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:p-6">
              <div className="flex justify-end content-center">
                <button
                  type="button"
                  className="bg-white text-muted hover:text-secondary focus-ring-brand-inset"
                  onClick={() => onClose()}
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
              {children}
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  )

  return (
    <Context.Provider value={{ visible, onClose, onConfirm }}>
      {isHydrated ? createPortal(dialog, document.body) : null}
    </Context.Provider>
  )
}

export type ModalTitleProps = {
  text: string
  headingLevel?: 2 | 3 | 4
}

export const ModalTitle = ({ text, headingLevel = 2 }: ModalTitleProps) => {
  const headingTag = `h${headingLevel}` as 'h2' | 'h3' | 'h4'

  return (
    <Dialog.Title as={headingTag} className="text-lg font-semibold leading-6 text-heading mr-4">
      {text}
    </Dialog.Title>
  )
}

export type ModalContentProps = PropsWithChildren

export const ModalContent = ({ children }: ModalContentProps) => {
  return <div className="mt-2">{children}</div>
}

export type ModalDismissButtonProps = {
  text: string
  variant?: 'primary' | 'secondary'
}

export type ModalConfirmButtonProps = {
  text: string
}

export const ModalDismissButton = ({ text, variant = 'primary' }: ModalDismissButtonProps) => {
  const { onClose } = useContext(Context)
  return (
    <div className="mt-5 sm:mt-6">
      <Button block align="none" variant={variant} onClick={onClose} label={text} />
    </div>
  )
}

export const ModalConfirmButton = ({ text }: ModalConfirmButtonProps) => {
  const { onConfirm } = useContext(Context)
  return (
    <div className="mt-5 sm:mt-6">
      <Button block align="none" variant="primary" onClick={onConfirm} label={text} />
    </div>
  )
}
